import {
  dragFillRect,
  fillRange,
  fillSource,
  parseSetCount,
  parseTsv,
  pasteMatrix,
  readRange,
  toTsv,
  writeRange,
  type CellPos,
  type CellRect,
  type CellRefusal,
  type Draft,
  type DraftGridLens,
  type DraftLineOptions,
  type MaxForProposal,
  type RangeWrite,
  type SlotDraft,
} from "@heraion/shared";

import type { TFn } from "../i18nTypes";
import type { ToastAction } from "../primitives/Toast";
import type { ProgramEditorV3Props } from "./types";

/** What the grid's writes need of the editor: its lock-guarded setDraft, its
 *  Undo action, the done rounds, and the «Sets» lens's own write
 *  (useRoundSetWrites). */
export type GridWriteDeps = Pick<ProgramEditorV3Props, "setDraft" | "showToast"> & {
  undo: ToastAction;
  isRoundLocked: (week: number) => boolean;
  refuse: (week?: number) => void;
  setCount: (slotIdx: number, week: number, n: number) => void;
};

/** The words of a refusal, for a toast and for a menu item's hint. */
export const cellWhy = (t: TFn, r: CellRefusal): string =>
  ({
    setsOnly: t("editorV3.grid.why.setsOnly", { defaultValue: "only the Sets lens can be written" }),
    off: t("editorV3.grid.why.off", { defaultValue: "the exercise is not in that round" }),
    locked: t("editorV3.grid.why.locked", { defaultValue: "the round is done" }),
    parse: t("editorV3.grid.why.parse", { defaultValue: "not a value for this lens" }),
    lastSet: t("editorV3.grid.why.lastSet", { defaultValue: "a round keeps at least one set" }),
    mixedBound: t("editorV3.grid.why.mixedBound", { defaultValue: "a timed set and a reps set would swap places" }),
  })[r];

type Args = GridWriteDeps & {
  draft: Draft;
  sIdx: number;
  lineOptions: DraftLineOptions;
  maxOf: (slot: SlotDraft) => MaxForProposal | undefined;
};

// The wide grid's writes (#336 slice 6 part 2b, brief §8.3), modelled on
// useRoundSetWrites: the Sets count typed in one cell, and the block ones
// (paste, fill down or right, the fill handle). Only the Sets lens is written:
// any other refuses with one reason. A cell holds for its round only. A block
// is one setDraft, so one Undo; the cells that refuse keep their value and the
// toast counts them. Done rounds are left out of a block; a block that
// touches nothing else gets the lock's own «Unlock» toast.
export type RoundCellWrites = ReturnType<typeof roundCellWrites>;

/** The writes, for a host with its own `t` that may have none (#358). */
export function roundCellWrites(t: TFn, { draft, setDraft, showToast, undo, isRoundLocked, refuse, setCount, sIdx, lineOptions, maxOf }: Args) {
  const why = (r: CellRefusal) => cellWhy(t, r);
  const whyAll = (skipped: readonly CellRefusal[]) => [...new Set(skipped)].map(why).join(", ");
  const nothing = (reasons: readonly CellRefusal[]) =>
    showToast(t("editorV3.grid.nothingWritten", { why: whyAll(reasons), defaultValue: "Nothing written: {{why}}." }));

  const block = (run: (d: Draft) => RangeWrite) => {
    const res = run(draft);
    const { written, skipped } = res;
    if (written === 0) {
      if (skipped.length > 0 && skipped.every((r) => r === "locked")) return refuse();
      if (skipped.length > 0) nothing(skipped);
      return;
    }
    setDraft((d) => run(d).draft);
    const parts = [
      t("editorV3.grid.cellsWritten", { count: written, defaultValue: "{{count}} cells written, each in its round only." }),
      skipped.length > 0
        ? t("editorV3.grid.cellsSkipped", {
            count: skipped.length,
            why: whyAll(skipped),
            defaultValue: "{{count}} left as they were: {{why}}.",
          })
        : null,
    ];
    showToast(parts.filter(Boolean).join(" "), undo);
  };

  // Only the Sets lens is written: any other says so and writes nothing.
  const setsOnly = (lens: DraftGridLens): boolean => {
    if (lens === "sets") return true;
    nothing(["setsOnly"]);
    return false;
  };

  const copied = (rect: CellRect) => {
    const count = (rect.bottom - rect.top + 1) * (rect.right - rect.left + 1);
    showToast(t("editorV3.grid.cellsCopied", { count, defaultValue: "{{count}} cells copied." }));
  };

  return {
    /** The open cell's Sets field: the count as typed. A done round gets the
     *  lock's refusal, with «Unlock», from useRoundSetWrites. */
    writeOne: (at: CellPos, text: string) => {
      const n = parseSetCount(text);
      if (n !== null) setCount(at.row, at.col, n);
    },
    /** The block as a spreadsheet copies it, and a toast saying how much; the
     *  menu's passes `quiet` and says it once the clipboard took it. */
    copy: (lens: DraftGridLens, rect: CellRect, quiet = false): string => {
      if (!quiet) copied(rect);
      return toTsv(readRange(draft, sIdx, rect, lens, lineOptions, maxOf));
    },
    copied,
    paste: (lens: DraftGridLens, rect: CellRect, text: string) => {
      if (!setsOnly(lens)) return;
      const matrix = pasteMatrix(rect, parseTsv(text));
      if (matrix.length === 0) return;
      block((d) => writeRange(d, sIdx, { row: rect.top, col: rect.left }, matrix, lens, isRoundLocked));
    },
    fill: (lens: DraftGridLens, rect: CellRect, dir: "down" | "right") => {
      if (setsOnly(lens)) block((d) => fillRange(d, sIdx, fillSource(rect, dir), rect, lens, lineOptions, maxOf, isRoundLocked));
    },
    /** The fill handle let go on cell `to`. */
    dragFill: (lens: DraftGridLens, rect: CellRect, to: CellPos) => {
      const dest = dragFillRect(rect, to);
      if (setsOnly(lens) && dest) block((d) => fillRange(d, sIdx, rect, dest, lens, lineOptions, maxOf, isRoundLocked));
    },
  };
}
